import { Info } from "lucide-react";

import type { StoreSelectorValue } from "@/@types/campaign";
import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CampaignType } from "@/constants/campaign-types";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";

import { CampaignConflictModalContent } from "../../components/campaign-conflict-modal";
import { CAMPAIGN_TYPES } from "../../components/edit-campaign/constants";
import { useAllStoreCodes } from "../../hooks/use-all-store-codes";
import { normalizeStoreSelection } from "../../utils/pro014-stores-diff";
import { leavesTooFewActiveItems, pro014StoreScope } from "../../utils/pro014-update-diff";
import { PRO014_STEP, PRO014_TOTAL_STEPS, PRO014_WIZARD_TEXTS } from "../constants";
import { useConflictResolution } from "../hooks/use-conflict-resolution";
import { usePro014Submit } from "../hooks/use-pro014-submit";
import { usePro014WizardState } from "../hooks/use-pro014-wizard-state";
import type {
  Pro014CampaignData,
  Pro014EditSource,
  Pro014Submission,
  Pro014WizardMode,
  Pro014WizardState,
} from "../types";
import { CampaignPreviewHeader } from "./campaign-preview-header";
import { QueuedCreationDialog } from "./queued-creation-dialog";
import StepIndicator from "./step-indicator";
import { Pro014EditStoresStep } from "./steps-pro014/edit-stores-step";
import StepFive from "./steps-pro014/step-five";
import StepFour from "./steps-pro014/step-four";
import StepOne from "./steps-pro014/step-one";
import StepThree from "./steps-pro014/step-three";
import StepTwo from "./steps-pro014/step-two";

interface Pro014WizardProps {
  mode: Pro014WizardMode;
  initialData: Pro014CampaignData;
  editSource?: Pro014EditSource;
  onChangeType?: (type: CampaignType) => void;
}

export function Pro014Wizard({
  mode,
  initialData,
  editSource,
  onChangeType,
}: Pro014WizardProps): JSX.Element {
  const wizard: Pro014WizardState = usePro014WizardState(initialData);
  const { handleModal } = useDialogModal();
  const { data } = wizard;

  const openConflictModal = (
    conflicts: CampaignConflictItem[],
    unresolvableItemCodes?: string[],
  ): void => {
    handleModal({
      isOpen: true,
      title: CampaignConflictModalContent.title,
      element: (
        <CampaignConflictModalContent
          initialConflicts={conflicts}
          discardItemLocally={wizard.discardItem}
          onStageRemoval={wizard.stageRemoval}
          onUnstageRemoval={wizard.unstageRemoval}
          onConflictsChange={wizard.setConflicts}
          onGoBack={() => wizard.goTo(PRO014_STEP.PRODUCTS)}
          unresolvableItemCodes={unresolvableItemCodes}
        />
      ),
    });
  };

  const { handleRemoveFromPrevious } = useConflictResolution({
    conflicts: wizard.conflicts,
    setConflicts: wizard.setConflicts,
    discardItemLocally: wizard.discardItem,
    stageRemoval: wizard.stageRemoval,
    onAllResolved: () => undefined,
  });

  const submission: Pro014Submission = usePro014Submit({
    mode,
    editSource,
    wizard,
    openConflictModal,
  });

  const allStoreCodes: number[] = useAllStoreCodes();
  const storeScope: StoreSelectorValue = editSource
    ? pro014StoreScope(editSource.detail)
    : normalizeStoreSelection(data, allStoreCodes);

  const renderStoresStep: () => JSX.Element = editSource
    ? (): JSX.Element => (
        <Pro014EditStoresStep
          campaignCode={editSource.campaignCode}
          onBack={wizard.goBack}
          onNext={wizard.goNext}
        />
      )
    : (): JSX.Element => (
        <StepTwo
          data={{ allStores: data.allStores, stores: data.stores }}
          updateData={wizard.updateData}
          nextStep={wizard.goNext}
          prevStep={wizard.goBack}
        />
      );

  const stepViews: Record<number, () => JSX.Element> = {
    [PRO014_STEP.INFO]: () => (
      <StepOne
        data={{ description: data.description, startDate: data.startDate, endDate: data.endDate }}
        updateData={wizard.updateData}
        nextStep={wizard.goNext}
        onChangeType={onChangeType}
      />
    ),
    [PRO014_STEP.STORES]: renderStoresStep,
    [PRO014_STEP.DISCOUNT]: () => (
      <StepThree
        data={{
          takeQuantity: data.takeQuantity,
          payQuantity: data.payQuantity,
          discountValue: data.discountValue,
          discountType: data.discountType,
          paymentMethod: data.paymentMethod,
          baseValue: data.baseValue,
        }}
        updateData={wizard.updateData}
        nextStep={wizard.goNext}
        prevStep={wizard.goBack}
      />
    ),
    [PRO014_STEP.PRODUCTS]: () => (
      <StepFour
        data={{ items: data.items, gradeRestricted: data.gradeRestricted }}
        updateData={wizard.updateData}
        nextStep={wizard.goNext}
        prevStep={wizard.goBack}
        campaignStatus={editSource?.detail.status}
        conflicts={wizard.conflicts}
        conflictFilter={wizard.conflictFilter}
        onConflictFilterChange={wizard.setConflictFilter}
        onRemoveFromPrevious={handleRemoveFromPrevious}
        leavesTooFewActive={leavesTooFewActiveItems(editSource, data.items)}
      />
    ),
    [PRO014_STEP.REVIEW]: () => (
      <StepFive
        data={{ ...data, ...storeScope }}
        prevStep={wizard.goBack}
        onSubmit={submission.submit}
        isLoading={submission.isSubmitting}
        mode={mode}
        conflicts={wizard.conflicts}
        onEditProducts={() => wizard.goTo(PRO014_STEP.PRODUCTS)}
        validationProgress={submission.validationProgress}
      />
    ),
  };

  return (
    <div className="mx-auto w-full max-w-4xl">
      {editSource && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>{PRO014_WIZARD_TEXTS.EDIT_MODE(editSource.campaignCode)}</AlertDescription>
        </Alert>
      )}

      <StepIndicator
        currentStep={wizard.currentStep}
        totalSteps={PRO014_TOTAL_STEPS}
        onStepClick={wizard.goTo}
        requireConfirmationFrom={PRO014_STEP.STORES}
      >
        <CampaignPreviewHeader
          campaignType={CAMPAIGN_TYPES.PRO014}
          description={data.description}
          startDate={data.startDate}
          endDate={data.endDate}
          takeQuantity={data.takeQuantity}
          payQuantity={data.payQuantity}
          discountValue={data.discountValue}
          discountType={data.discountType}
          productsCount={data.items.length}
        />
      </StepIndicator>

      <div className="mt-4">{stepViews[wizard.currentStep]?.()}</div>

      <QueuedCreationDialog
        open={submission.queuedCreation !== null}
        jobId={submission.queuedCreation?.jobId ?? null}
        itemsCount={submission.queuedCreation?.itemsCount ?? 0}
        onClose={submission.closeQueuedCreation}
      />
    </div>
  );
}
